// In order to keep core.module.ts as small as possible, router functionality is kept in this file.
// This is not done with other modules because it is conventient to have 'ng generate component' automatically add a component to
// the appropriate module by running it in that directory.

import { RouterModule, Routes } from '@angular/router';
import { QuicklinkStrategy } from 'ngx-quicklink';

import { PermissionProfileEnum } from './enums/generated/PermissionProfileEnum';
import { AuthGuard } from './security/auth.guard';
import { Permission, PermissionConst } from './security/generated/Permission';

// This is the root of our navigation menu route definitions.
// Exported so core-routing.spec.ts can pin the gates on the routes a restricted profile
// must be able to open — a client gate that drifts from the server boundary is what
// refused a Witness the Take Possession screen the server was perfectly willing to serve.
export const routes: Routes = [
    // #region standalone components
    {
        path: 'admin/global-audit-list'
        , canActivate: [ AuthGuard ]
        , data: { security: PermissionConst.globalAuditHistory }
        , loadComponent: () => import('../admin/global-audit-list/global-audit-list.component').then(m => m.GlobalAuditListComponent) 
    }
    , {
		// Integration partner (API user) Settings: its integration client page without the API
		// Settings section, which is at Admin > Developer > API Settings (admin.module.ts).
		path: 'company/info/:id'
		, canActivate: [ AuthGuard ]
		, data: { security: PermissionConst.coreFeatures, section: 'details' }
        , loadComponent: () => import('../sysadmin/integrations/integrations-api-client-edit/integrations-api-client-edit.component')
            .then(m => m.IntegrationsApiClientEditComponent)
	}
    , { 
		path: 'sysadmin/customers/p-list'
		, canActivate: [ AuthGuard ] 
        , loadComponent: () => import('../sysadmin/clients/client-list/client-list.component').then(m => m.ClientListComponent) 
	}
    , {
		path: 'sysadmin/states/list'
		, canActivate: [ AuthGuard ]
		, data: { security: PermissionConst.clients }
        , loadComponent: () => import('../sysadmin/states/states-list/states-list.component').then(m => m.StatesListComponent) 
	}
    , {
		path: 'eula'
        , loadComponent: () => import('../sysadmin/footer/view-eula-footer/view-eula-footer.component').then(m => m.ViewEulaFooterComponent) 
	}
    , { 
		path: 'sysadmin/jobs/history'
		, canActivate: [ AuthGuard ]
        , loadComponent: () => import('../silkier/silkier-history/silkier-history.component').then(m => m.SilkierHistoryComponent) 
	}
    , { 
		path: 'admin/users/edit/details/:id'
		, canActivate: [ AuthGuard ]
		// S2N-1420: this root-level entry wins over the lazy admin module's copy (users.module.ts),
		// so it must carry the same gate or the module's ownAccountSecurity is never consulted.
        , data: { security: PermissionConst.ownAccountSecurity }
        , loadComponent: () => import('../admin/users/edit-user/edit-user.component').then(m => m.EditUserComponent)
	}
    , { 
		path: ''
        , loadComponent: () => import('./../dashboard/dashboard.component').then(m => m.DashboardComponent) 
	}
    , { 
		path: 'dashboard'
		, canActivate: [ AuthGuard ] 
        , loadComponent: () => import('./../dashboard/dashboard.component').then(m => m.DashboardComponent) 
	}
    , { 
		path: 'activate-cvs'
		, canActivate: [ AuthGuard ] 
        , loadComponent: () => import('./../dashboard/activate-temp-cvs/activate-temp-cvs.component').then(m => m.ActivateTempCvsComponent)
	}
    , {
		// S2N-3075: a Witness reaches this screen too, and a Witness holds no CoreFeatures — the
		// whole point of the story's authorization boundary. Gating on coreFeatures alone refused
		// the Take Possession button on the PQA home the dashboard had just rendered for them.
		//
		// The test is the profile, not Permission.WitnessPossession: that flag is true for every
		// profile (it is the key the [AllowWitnessPossession] marker turns server-side), so
		// reading it here would gate on nothing and would also admit S2N-3076's View Only.
		//
		// This opens the ROUTE, never the capability. Whether this Witness may take possession
		// right now — the agency flag, their agency-specific role, active membership, inventory
		// association — is decided server-side on every endpoint this page calls, by
		// IInventoryService.CheckWitnessCustodyAccessAsync / CheckWitnessCustodyAsync. A Witness
		// with no custody experience never sees the button: DashboardComponent asks
		// GetWitnessCustodyAccess first and lands them on their account page instead.
		path: 'take-possession'
		, canActivate: [ AuthGuard ]
		, data: {
			security: (sec: Permission) =>
				sec.coreFeatures
				|| sec.permissionProfile === PermissionProfileEnum.Witness
		}
        , loadComponent: () => import('./../dashboard/pqa-mobile/pages/pqa-take-possession-page/pqa-take-possession-page.component')
            .then(m => m.PqaTakePossessionPageComponent)
	}
    , {
		// S2N-3075: the "Past Activity in S2" card is on the PQA home a possession-enabled Witness
		// now lands on, so the route has to admit them for the same reason /take-possession does.
		// The endpoint behind it is self-scoped to the caller and re-decides for itself.
		path: 'my-activity'
		, canActivate: [ AuthGuard ]
		, data: {
			security: (sec: Permission) =>
				sec.coreFeatures
				|| sec.permissionProfile === PermissionProfileEnum.Witness
		}
        , loadComponent: () => import('./../dashboard/pqa-mobile/pages/pqa-my-activity/pqa-my-activity.component')
            .then(m => m.PqaMyActivityComponent)
	}
    , {
		// Manager+ Receive quick action (S2N-3057). Route data is coreFeatures on purpose:
		// the real authorization is ManageUsers on every /api/PqaMobile manager endpoint,
		// so a non-manager deep link renders the page's own access message instead of a
		// misleading route bounce.
		path: 'receive'
		, canActivate: [ AuthGuard ]
		, data: { security: PermissionConst.coreFeatures }
        , loadComponent: () => import('./../dashboard/pqa-mobile/pages/pqa-receive/pqa-receive.component')
            .then(m => m.PqaReceiveComponent)
	}
    , {
		// Manager+ Restock quick action (S2N-3057). Same gating rationale as /receive above.
		path: 'restock'
		, canActivate: [ AuthGuard ]
		, data: { security: PermissionConst.coreFeatures }
        , loadComponent: () => import('./../dashboard/pqa-mobile/pages/pqa-restock/pqa-restock.component')
            .then(m => m.PqaRestockComponent)
	}
    , {
		// The medication name travels as the ?group QUERY parameter, never as a path segment:
		// real names contain slashes ("BUPRENORPHINE HYDROCHLORIDE/NALOXONE") and a %2F inside a
		// segment is at the mercy of the URL serializer. It is never an authorization key either
		// way — the server authorizes on the inventory being one the caller currently possesses.
		// S2N-3075: a possession-enabled Witness opens these cards too - they are the medications
		// in the stock they are currently holding, which is part of the custody experience rather
		// than the general product browsing the story puts out of their reach. Refusing here was
		// the confusing kind of failure: the guard blocks the navigation before any request is
		// made, so every network call on the page still reported 200 while a toast said access
		// denied.
		path: 'medication/:inventorySiteId'
		, canActivate: [ AuthGuard ]
		, data: {
			security: (sec: Permission) =>
				sec.coreFeatures
				|| sec.permissionProfile === PermissionProfileEnum.Witness
		}
        , loadComponent: () => import('./../dashboard/pqa-mobile/pages/pqa-medication-detail/pqa-medication-detail.component')
            .then(m => m.PqaMedicationDetailComponent)
	}
    , { 
		path: 'login'
		, loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent)
	}
	, { 
		path: 'release-notes'
		, canActivate: [ AuthGuard ] 
        , loadComponent: () => import('./pages/web-release-notes/web-release-notes.component').then(m => m.WebReleaseNotesComponent)
	}
    , { 
		path: 'forgot-password'
		, loadComponent: () => import('./pages/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent) 
	}
    , { 
		path: 'change-password/:id'
		, loadComponent: () => import('./pages/change-password/change-password.component').then(m => m.ChangePasswordComponent) 
	}
    , { 
		path: 'privacy-policy'
        , loadComponent: () => import('../sysadmin/footer/view-privacy-policy-footer/view-privacy-policy-footer.component')
            .then(m => m.ViewPrivacyPolicyFooterComponent) 
	}
    , { 
		path: 'terms-conditions'
		, loadComponent: () => import('../sysadmin/footer/view-terms-conditions-footer/view-terms-conditions-footer.component')
            .then(m => m.ViewTermsConditionsFooterComponent) 
	}
	, {
		path: 'forms/ny-form-3848'
		, loadComponent: () => import('./pages/public/forms/public-ny-form-3848/public-ny-form-3848.component').then(m => m.PublicNyForm3848Component)
	}
    , {
		path: 'silence-biennial-report-reminder/:clientGuid/:userGuid'
		, loadComponent: () => import('./pages/public/silence-biennial-report-reminder/silence-biennial-report-reminder.component')
			.then(m => m.SilenceBiennialReportReminderComponent)
	} 	
    // #endregion

    // #region modules
    , { 
		path: 'knox'
		, canLoad: [ AuthGuard ]
		, loadChildren: () => import('../knox/knox.module').then(m => m.KnoxModule) 
	}
	, { 
		path: 'admin'
		, canLoad: [ AuthGuard ]
		, loadChildren: () => import('../admin/admin.module').then(m => m.AdminModule) 
	}
	, { 
		path: 'sysadmin'
		, canLoad: [ AuthGuard ]
		, loadChildren: () => import('../sysadmin/sysadmin.module').then(m => m.SysadminModule) 
	}
	, { 
		path: 'reports'
		, canLoad: [ AuthGuard ]
		, loadChildren: () => import('../reports/reports.module').then(m => m.ReportsModule) 
	}
	, { 
		path: 'forms'
		, canLoad: [ AuthGuard ]
		, loadChildren: () => import('../forms/forms.module').then(m => m.FormsModule) 
	} 
    , { 
		path: 'inventory'
		, canLoad: [ AuthGuard ]
		, loadChildren: () => import('../inventory/inventory.module').then(m => m.InventoryModule) 
	}
    , {
		path: 'shared'
		, canActivate: [ AuthGuard ]
		, canLoad: [ AuthGuard ]
		, loadChildren: () => import('../shared/shared.module').then(m => m.SharedModule)
	}  
    // #endregion
    , { 
		path: '**'
		, redirectTo: '' 
	}
];

export class CoreRouting {
	static readonly declarations = [];
	static readonly imports = [
		RouterModule.forRoot(
			routes
			, {
				scrollPositionRestoration: 'enabled'
				, preloadingStrategy: QuicklinkStrategy
				, anchorScrolling: 'enabled'
			}
		)
	];
	static readonly exports = [ RouterModule ];
	static readonly providers = [ AuthGuard ];
}
